import {
  Activity,
  LayoutDashboard,
  Wallet,
  ShieldAlert,
  Inbox,
  MessageSquare,
  Wrench,
  Palette,
  Lock,
  BookOpen,
  Users,
  Mic2,
  Sparkles,
  Newspaper,
  CreditCard,
  Mail,
  Target,
  Handshake,
  Briefcase,
  type LucideIcon,
} from "lucide-react";
import { PERMISSIONS, type Permission } from "@/lib/permissions/catalog";

/**
 * Definición centralizada de la navegación de Nexus.
 *
 * Cada `NavGroup` aparece como item en el sidebar. Si tiene `children`,
 * abre una sección con sub-rutas. Si no, lleva directo al `to`.
 *
 * `requiredPermission` controla la visibilidad del item — un user sin el
 * permiso NO ve el item en el sidebar (pero el backend igual valida en
 * cada request).
 */

export type NavItem = {
  label: string;
  to: string;
  requiredPermission: Permission | string;
  badge?: number;
};

export type NavGroup = {
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
  requiredPermission: Permission | string;
  children?: NavItem[];
  /** Si true, el grupo va al fondo del sidebar (Docs). */
  pinBottom?: boolean;
  /**
   * Si true, el permiso se exige EXPLÍCITO (no vale el wildcard "*" del
   * Owner). Lo usa "Mi trabajo": sólo para quien de verdad es reclutador.
   */
  exactPermission?: boolean;
};

export const NAVIGATION: NavGroup[] = [
  {
    id: "pulse",
    label: "Pulso",
    icon: Activity,
    to: "/pulse",
    requiredPermission: PERMISSIONS.PULSE_VIEW,
  },
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    to: "/dashboard",
    requiredPermission: PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW,
    children: [
      { label: "Ejecutivo", to: "/dashboard/executive", requiredPermission: PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW },
      { label: "Operativo", to: "/dashboard/operational", requiredPermission: PERMISSIONS.DASHBOARD_OPERATIONAL_VIEW },
      { label: "Analítico", to: "/dashboard/analytical", requiredPermission: PERMISSIONS.DASHBOARD_ANALYTICAL_VIEW },
      { label: "App y música", to: "/dashboard/app", requiredPermission: PERMISSIONS.DASHBOARD_ANALYTICAL_VIEW },
    ],
  },
  {
    id: "producers",
    label: "Productores",
    icon: Users,
    to: "/producers",
    requiredPermission: PERMISSIONS.PRODUCERS_MGMT_VIEW,
  },
  {
    id: "artists",
    label: "Artistas",
    icon: Mic2,
    to: "/artists",
    requiredPermission: PERMISSIONS.ARTISTS_MGMT_VIEW,
  },
  {
    id: "settlements",
    label: "Liquidaciones",
    icon: Wallet,
    to: "/settlements",
    requiredPermission: PERMISSIONS.SETTLEMENTS_VIEW,
    children: [
      { label: "Tablero", to: "/settlements/board", requiredPermission: PERMISSIONS.SETTLEMENTS_VIEW },
      { label: "Pendientes", to: "/settlements/pending", requiredPermission: PERMISSIONS.SETTLEMENTS_VIEW },
      { label: "En revisión", to: "/settlements/review", requiredPermission: PERMISSIONS.SETTLEMENTS_VIEW },
      { label: "Disputas", to: "/settlements/disputes", requiredPermission: PERMISSIONS.SETTLEMENTS_VIEW },
      { label: "Por transferir", to: "/settlements/to-transfer", requiredPermission: PERMISSIONS.SETTLEMENTS_VIEW },
      { label: "Histórico", to: "/settlements/history", requiredPermission: PERMISSIONS.SETTLEMENTS_VIEW },
      { label: "Productores", to: "/settlements/producers", requiredPermission: PERMISSIONS.SETTLEMENTS_VIEW },
      { label: "Reportes", to: "/settlements/reports", requiredPermission: PERMISSIONS.SETTLEMENTS_REPORTS_VIEW },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    icon: ShieldAlert,
    to: "/governance",
    requiredPermission: PERMISSIONS.MODERATION_VIEW,
    children: [
      { label: "Moderación de eventos", to: "/governance/moderation", requiredPermission: PERMISSIONS.MODERATION_VIEW },
      { label: "Incidentes", to: "/governance/incidents", requiredPermission: PERMISSIONS.INCIDENTS_VIEW },
      { label: "Lista negra global", to: "/governance/blacklist", requiredPermission: PERMISSIONS.BLACKLIST_VIEW },
      { label: "Apelaciones", to: "/governance/appeals", requiredPermission: PERMISSIONS.BLACKLIST_APPEALS_RESOLVE },
    ],
  },
  {
    id: "applications",
    label: "Aplicaciones",
    icon: Inbox,
    to: "/applications",
    requiredPermission: PERMISSIONS.APPS_PRODUCERS_VIEW,
    children: [
      { label: "Bandeja", to: "/applications/inbox", requiredPermission: PERMISSIONS.APPS_PRODUCERS_VIEW },
      { label: "Productores", to: "/applications/producers", requiredPermission: PERMISSIONS.APPS_PRODUCERS_VIEW },
      { label: "Artistas", to: "/applications/artists", requiredPermission: PERMISSIONS.APPS_ARTISTS_VIEW },
      { label: "Proveedores", to: "/applications/providers", requiredPermission: PERMISSIONS.APPS_PROVIDERS_VIEW },
      { label: "Trabajo", to: "/applications/jobs", requiredPermission: PERMISSIONS.APPS_JOBS_VIEW },
    ],
  },
  {
    id: "seguimiento",
    label: "Seguimiento",
    icon: Target,
    to: "/seguimiento",
    requiredPermission: PERMISSIONS.SEGUIMIENTO_VIEW,
    children: [
      { label: "Funnel", to: "/seguimiento/funnel", requiredPermission: PERMISSIONS.SEGUIMIENTO_VIEW },
      { label: "Invitaciones", to: "/seguimiento/invitations", requiredPermission: PERMISSIONS.SEGUIMIENTO_VIEW },
      { label: "Activos", to: "/seguimiento/activos", requiredPermission: PERMISSIONS.SEGUIMIENTO_VIEW },
      { label: "Validación", to: "/seguimiento/validacion", requiredPermission: PERMISSIONS.SEGUIMIENTO_VIEW },
      { label: "Tareas equipo", to: "/seguimiento/tareas", requiredPermission: PERMISSIONS.SEGUIMIENTO_VIEW },
      { label: "Sprints", to: "/seguimiento/sprints", requiredPermission: PERMISSIONS.SEGUIMIENTO_VIEW },
      { label: "Áreas", to: "/seguimiento/areas", requiredPermission: PERMISSIONS.SEGUIMIENTO_VIEW },
      { label: "Métricas", to: "/seguimiento/metricas", requiredPermission: PERMISSIONS.SEGUIMIENTO_VIEW },
    ],
  },
  {
    id: "recruiter-self",
    label: "Mi trabajo",
    icon: Briefcase,
    to: "/reclutador",
    requiredPermission: PERMISSIONS.RECRUITERS_SELF,
    exactPermission: true,
  },
  {
    id: "recruiters",
    label: "Reclutadores",
    icon: Handshake,
    to: "/reclutadores",
    requiredPermission: PERMISSIONS.RECRUITERS_VIEW,
  },
  {
    id: "communications",
    label: "Comunicaciones",
    icon: MessageSquare,
    to: "/communications",
    requiredPermission: PERMISSIONS.COMMS_PUBLIC_MESSAGES_VIEW,
    children: [
      { label: "Mensajes públicos", to: "/communications/messages", requiredPermission: PERMISSIONS.COMMS_PUBLIC_MESSAGES_VIEW },
      { label: "Contacto público", to: "/communications/contact", requiredPermission: PERMISSIONS.COMMS_CONTACT_VIEW },
      { label: "Comunidad", to: "/communications/community", requiredPermission: PERMISSIONS.COMMS_COMMUNITY_VIEW },
    ],
  },
  {
    id: "operations",
    label: "Operaciones",
    icon: Wrench,
    to: "/operations",
    requiredPermission: PERMISSIONS.OPS_MP_HEALTH_VIEW,
    children: [
      { label: "Salud MP", to: "/operations/mp-health", requiredPermission: PERMISSIONS.OPS_MP_HEALTH_VIEW },
      { label: "Auditoría MP", to: "/operations/mp-audit", requiredPermission: PERMISSIONS.OPS_MP_AUDIT_VIEW },
      { label: "Feature Flags", to: "/operations/features", requiredPermission: PERMISSIONS.OPS_FEATURES_VIEW },
      { label: "Explorador de datos", to: "/operations/data", requiredPermission: PERMISSIONS.OPS_DATA_VIEW },
      { label: "Documentos SII", to: "/operations/tax", requiredPermission: PERMISSIONS.TAX_VIEW },
      { label: "Configuración SII", to: "/operations/tax-config", requiredPermission: PERMISSIONS.TAX_VIEW },
    ],
  },
  {
    id: "content",
    label: "Comunidad",
    icon: Newspaper,
    to: "/content",
    requiredPermission: PERMISSIONS.CONTENT_VIEW,
    children: [
      { label: "Contenido", to: "/content", requiredPermission: PERMISSIONS.CONTENT_VIEW },
      { label: "Reseñas", to: "/content/reviews", requiredPermission: PERMISSIONS.CONTENT_MANAGE },
    ],
  },
  {
    id: "newsletter",
    label: "Newsletter",
    icon: Mail,
    to: "/newsletter",
    requiredPermission: PERMISSIONS.NEWSLETTER_MANAGE,
  },
  {
    id: "billing",
    label: "Facturación",
    icon: CreditCard,
    to: "/billing",
    requiredPermission: PERMISSIONS.BILLING_SKUS_VIEW,
    children: [
      { label: "Precios & SKUs", to: "/billing", requiredPermission: PERMISSIONS.BILLING_SKUS_VIEW },
      { label: "Suscripciones", to: "/billing/entitlements", requiredPermission: PERMISSIONS.BILLING_ENTITLEMENTS_VIEW },
      { label: "Códigos promo", to: "/billing/promo-codes", requiredPermission: PERMISSIONS.PROMO_CODES_VIEW },
    ],
  },
  {
    id: "theme",
    label: "Tema",
    icon: Palette,
    to: "/theme",
    requiredPermission: PERMISSIONS.THEME_VIEW,
  },
  {
    id: "demo",
    label: "Cuenta Demo",
    icon: Sparkles,
    to: "/demo",
    requiredPermission: PERMISSIONS.DEMO_VIEW,
  },
  {
    id: "control",
    label: "Nexus Control",
    icon: Lock,
    to: "/control",
    requiredPermission: PERMISSIONS.NEXUS_CONTROL_VIEW,
    children: [
      { label: "Usuarios", to: "/control/users", requiredPermission: PERMISSIONS.NEXUS_CONTROL_MANAGE_USERS },
      { label: "Roles", to: "/control/roles", requiredPermission: PERMISSIONS.NEXUS_CONTROL_MANAGE_ROLES },
      { label: "Audit log", to: "/control/audit", requiredPermission: PERMISSIONS.NEXUS_CONTROL_VIEW_AUDIT },
    ],
  },
  {
    id: "docs",
    label: "Documentación",
    icon: BookOpen,
    to: "/docs",
    requiredPermission: PERMISSIONS.DOCS_VIEW,
    pinBottom: true,
  },
];
